<script lang="ts">
  /**
   * The frame the page templates share (not exported): a `FullPage` for the
   * `page` frame, or the game's own screen under a `ScreenHeader` for the
   * `screen` frame (`PageFrame` in `pageTypes.ts` says which is which), the
   * template's content, and an optional footer that stays in view.
   *
   * The footer follows the content while everything fits, and sticks to the
   * bottom edge once the content scrolls (`position: sticky`), on the page's
   * own ground, so the one thing a picker is for is never below the fold.
   *
   * `dismissible`, `onbackattempt` and `describedby` (since 1.7.0) are
   * `FullPage`'s, and apply to the `page` frame only.
   *
   * Test ids: `testid` on the dialog (`page`) or the screen (`screen`);
   * `testids.done` / `testids.back` on the way out (FullPage's and
   * ScreenHeader's defaults when absent).
   */
  import type { Snippet } from 'svelte';

  import FullPage from '../components/FullPage.svelte';
  import ScreenHeader from '../components/ScreenHeader.svelte';
  import type { SheetBackAttempt } from '../types.js';
  import type { PageDress, PageFrame } from './pageTypes.js';

  interface Props {
    readonly frame: PageFrame;
    readonly open: boolean;
    readonly title: string;
    readonly onclose: () => void;
    readonly dress: PageDress;
    readonly wide?: boolean;
    readonly leaveLabel?: string | undefined;
    /** The `screen` frame's content width from 1024px: 30rem or 36rem. */
    readonly measure?: 'narrow' | 'medium';
    readonly children: Snippet;
    readonly footer?: Snippet | undefined;
    /**
     * The `screen` frame moves focus to its title when it appears, unless
     * the content takes it (`false`: a join screen's code boxes). One
     * element asks for focus, never two racing for it.
     */
    readonly focusTitle?: boolean;
    readonly testid?: string | undefined;
    readonly testids?: { readonly done?: string; readonly back?: string } | undefined;
    // ── 1.7.0: `FullPage`'s ──
    // The `page` frame only: the `screen` frame is the game's routed screen,
    // whose Back and way out are the game's, and ignores all three.
    readonly dismissible?: boolean;
    readonly onbackattempt?: ((reason: SheetBackAttempt) => void) | undefined;
    readonly describedby?: string | undefined;
  }
  const {
    frame,
    open,
    title,
    onclose,
    dress,
    wide = false,
    leaveLabel,
    measure = 'narrow',
    children,
    footer,
    focusTitle = true,
    testid,
    testids,
    dismissible = true,
    onbackattempt,
    describedby,
  }: Props = $props();

  const pageTestids = $derived({
    ...(testids?.done !== undefined ? { done: testids.done } : {}),
    ...(testids?.back !== undefined ? { back: testids.back } : {}),
  });
  const sunken = $derived(frame === 'page' && dress === 'done');
</script>

{#snippet content()}
  {@render children()}
  {#if footer !== undefined}
    <div class="ui-frame-footer" class:ui-frame-footer--sunken={sunken}>
      {@render footer()}
    </div>
  {/if}
{/snippet}

{#if frame === 'page'}
  <FullPage
    {open}
    {title}
    {onclose}
    {dress}
    {wide}
    {leaveLabel}
    {testid}
    testids={pageTestids}
    {dismissible}
    {onbackattempt}
    {describedby}
  >
    {@render content()}
  </FullPage>
{:else if open}
  <section class="ui-frame-screen" aria-label={title} data-testid={testid}>
    <ScreenHeader
      {title}
      onback={onclose}
      backLabel={leaveLabel}
      backTestid={testids?.back}
      focusOnMount={focusTitle}
    />
    <div class="ui-frame-body ui-frame-body--{measure}">
      {@render content()}
    </div>
  </section>
{/if}

<style>
  .ui-frame-screen {
    box-sizing: border-box;
    height: 100svh;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    padding-top: env(safe-area-inset-top);
    background: var(--ui-ground);
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  .ui-frame-body {
    box-sizing: border-box;
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 0.25rem 1.25rem calc(1.5rem + env(safe-area-inset-bottom));
  }

  @media (min-width: 1024px) {
    .ui-frame-body {
      width: 100%;
      margin-inline: auto;
    }

    .ui-frame-body--narrow {
      max-width: 30rem;
    }

    .ui-frame-body--medium {
      max-width: 36rem;
    }
  }

  /* In view at the bottom once the content scrolls; on the ground it stands
     on, so what scrolls under it is hidden rather than drawn through. */
  .ui-frame-footer {
    position: sticky;
    bottom: 0;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-3);
    padding: var(--ui-space-4) 0 calc(var(--ui-space-3) + env(safe-area-inset-bottom));
    background: var(--ui-ground);
  }

  .ui-frame-footer--sunken {
    background: var(--ui-sunken);
  }
</style>
